import type { UserContextValue } from '@core/context';

import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { BaseUrlContext, UserContext } from '@core/context';
import useUserPermissions from '@core/hooks/useUserPermissions';
import { useProjectStore, useSuperHubStore } from '@core/store';
import classy from '@core/utils/classy';
import Button from '@ui/Button';
import Dropdown from '@ui/Dropdown';
import Icon from '@ui/Icon';
import Menu from '@ui/Menu';
import MenuItem from '@ui/Menu/Item';

import classes from '../../style.module.scss';

const UserMenu = () => {
  const { t } = useTranslation();
  const [enableSuggestedEdits, projectLayout] = useProjectStore(s => [
    s.data.suggested_edits === 'enabled',
    s.data.appearance.layout.style,
  ]);
  const [isSuperHubAdmin, isSuperHubEditor] = useSuperHubStore(s => [
    s.user.role === 'admin',
    s.user.role === 'editor',
  ]);
  const { permissions, user } = useContext(UserContext) as unknown as UserContextValue;
  const baseUrl = useContext(BaseUrlContext);
  // TODO: Remove this!
  // https://linear.app/readme-io/issue/RM-1250
  const cleanBaseUrl = baseUrl === '/' ? '' : baseUrl;
  const { isAdminUser } = useUserPermissions();
  const { pathname } = useLocation();

  const hideButtonLabel = projectLayout === 'compact';

  if (!permissions) {
    return (
      <Button
        className={classy(
          classes['Header-link'],
          classes.NavItem,
          classes.NavItem_md,
          'rm-Header-top-link',
          'rm-Header-top-link_login',
        )}
        ghost
        href={`/login?redirect_uri=${cleanBaseUrl}${pathname}`}
        target="_self"
      >
        {t('auth.logIn')}
      </Button>
    );
  }

  return (
    <Dropdown appendTo={() => document.body} className="rm-Header-top-link_login" justify="end" sticky>
      <Button
        {...(hideButtonLabel && { 'aria-label': 'Toggle user menu' })}
        bem={{ ['slate_text']: true }}
        className={classy(classes['Header-link'], classes.NavItem, classes.NavItem_md, 'rm-Header-top-link')}
      >
        {hideButtonLabel ? (
          <Icon aria-hidden="true" name="user-circle" size="md" wrapperClassName={`${classes['NavItem-icon']}`} />
        ) : (
          <>
            <span>{user?.name || 'Account'}</span>
            <Icon aria-hidden="true" className={`${classes['NavItem-chevron']}`} name="chevron-down" size="md" />
          </>
        )}
      </Button>
      <Menu>
        {!!isAdminUser && !isSuperHubAdmin && (
          <MenuItem href={`${cleanBaseUrl}/dash`} icon={<Icon name="lock" />} TagName="a">
            Admin Dashboard
          </MenuItem>
        )}
        {!!enableSuggestedEdits && !isSuperHubAdmin && !isSuperHubEditor && (
          <MenuItem href={`${cleanBaseUrl}/suggested-edits`} icon={<Icon name="suggested-edits" />} TagName="a">
            Suggested Edits
          </MenuItem>
        )}
        <MenuItem href={`/logout?redirect_uri=${cleanBaseUrl}${pathname}`} icon={<Icon name="log-out" />} TagName="a">
          {t('auth.logOut')}
        </MenuItem>
      </Menu>
    </Dropdown>
  );
};

export default UserMenu;
